import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useExperience } from '../../context/ExperienceContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { Button } from '../ui/Button';
import type { DarumaPose } from './DarumaBubble';

interface TourStep {
  page: string;
  target: string;
  pose: DarumaPose;
  titleKey: string;
  messageKey: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  isPageTransition?: boolean;
  isPremium?: boolean;
}

const ENTHUSIAST_STEPS: TourStep[] = [
  // Dashboard
  { page: '/', target: 'dashboard-welcome', pose: 'wave', titleKey: 'onboarding.spotDashboardTitle', messageKey: 'onboarding.spotDashboardWelcome', position: 'bottom' },
  { page: '/', target: 'dashboard-trips', pose: 'explain', titleKey: 'onboarding.spotDashboardTitle', messageKey: 'onboarding.spotDashboardTrips', position: 'auto' },
  { page: '/', target: 'dashboard-weekly-spot', pose: 'point', titleKey: 'onboarding.spotDashboardTitle', messageKey: 'onboarding.spotDashboardWeekly', position: 'auto' },
  { page: '/', target: 'dashboard-streak', pose: 'celebrate', titleKey: 'onboarding.spotDashboardTitle', messageKey: 'onboarding.spotDashboardStreak', position: 'auto' },
  { page: '/', target: 'nav-dream', pose: 'travel', titleKey: 'onboarding.spotDreamTitle', messageKey: 'onboarding.spotGoToDream', position: 'bottom', isPageTransition: true },
  // Dream — Explore
  { page: '/dream', target: 'explore-map', pose: 'explain', titleKey: 'onboarding.spotExploreTitle', messageKey: 'onboarding.spotExploreMap', position: 'bottom' },
  { page: '/dream', target: 'explore-search', pose: 'point', titleKey: 'onboarding.spotExploreTitle', messageKey: 'onboarding.spotExploreSearch', position: 'bottom' },
  { page: '/dream', target: 'explore-filters', pose: 'explain', titleKey: 'onboarding.spotExploreTitle', messageKey: 'onboarding.spotExploreFilters', position: 'bottom' },
  // Dream — Library
  { page: '/dream?tab=library', target: 'library-tab', pose: 'point', titleKey: 'onboarding.spotLibraryTitle', messageKey: 'onboarding.spotLibraryIntro', position: 'bottom' },
  { page: '/dream?tab=library', target: 'library-search', pose: 'explain', titleKey: 'onboarding.spotLibraryTitle', messageKey: 'onboarding.spotLibrarySearch', position: 'bottom' },
  { page: '/dream?tab=library', target: 'library-add-custom', pose: 'point', titleKey: 'onboarding.spotLibraryTitle', messageKey: 'onboarding.spotLibraryCustom', position: 'auto' },
  // Dream — Passport
  { page: '/dream?tab=passport', target: 'passport-tab', pose: 'celebrate', titleKey: 'onboarding.spotPassportTitle', messageKey: 'onboarding.spotPassportIntro', position: 'bottom' },
  { page: '/dream?tab=passport', target: 'passport-stamps', pose: 'explain', titleKey: 'onboarding.spotPassportTitle', messageKey: 'onboarding.spotPassportStamps', position: 'auto' },
  { page: '/dream?tab=passport', target: 'passport-rank', pose: 'point', titleKey: 'onboarding.spotPassportTitle', messageKey: 'onboarding.spotPassportRank', position: 'auto' },
  { page: '/dream?tab=passport', target: 'nav-travel', pose: 'travel', titleKey: 'onboarding.spotTravelTitle', messageKey: 'onboarding.spotGoToTravel', position: 'bottom', isPageTransition: true },
  // Travel
  { page: '/travel', target: 'travel-create-trip', pose: 'explain', titleKey: 'onboarding.spotTravelTitle', messageKey: 'onboarding.spotTravelCreate', position: 'auto' },
  { page: '/travel', target: 'travel-trips-list', pose: 'point', titleKey: 'onboarding.spotTravelTitle', messageKey: 'onboarding.spotTravelList', position: 'auto' },
  { page: '/travel', target: 'travel-today-tab', pose: 'explain', titleKey: 'onboarding.spotTravelTitle', messageKey: 'onboarding.spotTravelToday', position: 'bottom' },
  // Travel — premium features
  { page: '/travel', target: 'travel-create-trip', pose: 'explain', titleKey: 'onboarding.spotTravelTitle', messageKey: 'onboarding.spotTravelAiGenerate', position: 'auto', isPremium: true },
  { page: '/travel', target: 'travel-create-trip', pose: 'point', titleKey: 'onboarding.spotTravelTitle', messageKey: 'onboarding.spotTravelPremiumFeatures', position: 'auto', isPremium: true },
  { page: '/travel', target: 'nav-share', pose: 'travel', titleKey: 'onboarding.spotShareTitle', messageKey: 'onboarding.spotGoToShare', position: 'bottom', isPageTransition: true },
  // Share
  { page: '/share', target: 'share-community', pose: 'explain', titleKey: 'onboarding.spotShareTitle', messageKey: 'onboarding.spotShareCommunity', position: 'auto' },
  { page: '/share', target: 'share-leaderboard', pose: 'celebrate', titleKey: 'onboarding.spotShareTitle', messageKey: 'onboarding.spotShareLeaderboard', position: 'auto' },
  { page: '/share', target: 'nav-credits', pose: 'travel', titleKey: 'onboarding.spotCreditsTitle', messageKey: 'onboarding.spotGoToCredits', position: 'bottom', isPageTransition: true },
  // Credits — premium features
  { page: '/credits', target: 'credits-balance', pose: 'explain', titleKey: 'onboarding.spotCreditsTitle', messageKey: 'onboarding.spotCreditsBalance', position: 'auto', isPremium: true },
  { page: '/credits', target: 'credits-shop', pose: 'point', titleKey: 'onboarding.spotCreditsTitle', messageKey: 'onboarding.spotCreditsShop', position: 'auto', isPremium: true },
  // Closing
  { page: '/credits', target: 'credits-shop', pose: 'celebrate', titleKey: 'onboarding.spotCompleteTitle', messageKey: 'onboarding.spotComplete', position: 'auto' },
];

const CASUAL_STEPS: TourStep[] = [
  { page: '/', target: 'dashboard-welcome', pose: 'wave', titleKey: 'onboarding.spotDashboardTitle', messageKey: 'onboarding.spotDashboardWelcome', position: 'bottom' },
  { page: '/', target: 'dashboard-trips', pose: 'explain', titleKey: 'onboarding.spotDashboardTitle', messageKey: 'onboarding.spotDashboardTrips', position: 'auto' },
  { page: '/', target: 'nav-dream', pose: 'travel', titleKey: 'onboarding.spotDreamTitle', messageKey: 'onboarding.spotGoToDream', position: 'bottom', isPageTransition: true },
  { page: '/dream', target: 'explore-map', pose: 'explain', titleKey: 'onboarding.spotExploreTitle', messageKey: 'onboarding.spotCasualExploreMap', position: 'bottom' },
  { page: '/dream', target: 'explore-search', pose: 'point', titleKey: 'onboarding.spotExploreTitle', messageKey: 'onboarding.spotExploreSearch', position: 'bottom' },
  { page: '/dream?tab=library', target: 'library-tab', pose: 'point', titleKey: 'onboarding.spotLibraryTitle', messageKey: 'onboarding.spotCasualLibraryIntro', position: 'bottom' },
  { page: '/dream?tab=library', target: 'library-add-custom', pose: 'explain', titleKey: 'onboarding.spotLibraryTitle', messageKey: 'onboarding.spotLibraryCustom', position: 'auto' },
  { page: '/dream?tab=library', target: 'nav-travel', pose: 'travel', titleKey: 'onboarding.spotTravelTitle', messageKey: 'onboarding.spotGoToTravel', position: 'bottom', isPageTransition: true },
  { page: '/travel', target: 'travel-create-trip', pose: 'explain', titleKey: 'onboarding.spotTravelTitle', messageKey: 'onboarding.spotTravelCreate', position: 'auto' },
  { page: '/travel', target: 'travel-trips-list', pose: 'point', titleKey: 'onboarding.spotTravelTitle', messageKey: 'onboarding.spotCasualTravelList', position: 'auto' },
  { page: '/travel', target: 'travel-create-trip', pose: 'wave', titleKey: 'onboarding.spotCompleteTitle', messageKey: 'onboarding.spotCasualComplete', position: 'auto' },
];

const POPOVER_WIDTH = 360;
const POPOVER_HEIGHT_ESTIMATE = 220;
const MARGIN = 16;
const NAV_HEIGHT = 80;

function clampToViewport(top: number, left: number, vw: number, vh: number) {
  const safeLeft = Math.max(MARGIN, Math.min(left, vw - POPOVER_WIDTH - MARGIN));
  const safeTop = Math.max(NAV_HEIGHT, Math.min(top, vh - POPOVER_HEIGHT_ESTIMATE - MARGIN));
  return { top: safeTop, left: safeLeft };
}

function getPopoverPosition(rect: DOMRect, position: TourStep['position']) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let resolved = position || 'auto';
  if (resolved === 'auto') {
    if (rect.bottom + POPOVER_HEIGHT_ESTIMATE + MARGIN < vh) resolved = 'bottom';
    else if (rect.top - POPOVER_HEIGHT_ESTIMATE - MARGIN > NAV_HEIGHT) resolved = 'top';
    else if (rect.right + POPOVER_WIDTH + MARGIN < vw) resolved = 'right';
    else resolved = 'left';
  }

  let top: number;
  let left: number;
  switch (resolved) {
    case 'bottom':
      top = rect.bottom + MARGIN;
      left = rect.left;
      break;
    case 'top':
      top = rect.top - POPOVER_HEIGHT_ESTIMATE - MARGIN;
      left = rect.left;
      break;
    case 'right':
      top = rect.top;
      left = rect.right + MARGIN;
      break;
    case 'left':
    default:
      top = rect.top;
      left = rect.left - POPOVER_WIDTH - MARGIN;
      break;
  }

  return clampToViewport(top, left, vw, vh);
}

export function SpotlightTour() {
  const { t } = useTranslation('pages');
  const { user } = useAuth();
  const { isManga, isUkiyo, isMangaV3 } = useExperience();
  const { state, setPhase, updateState } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();

  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [imgFailed, setImgFailed] = useState(false);
  // null = haven't asked yet, true = include premium steps, false = hide them.
  const [showPremium, setShowPremium] = useState<boolean | null>(null);
  const lookupTimerRef = useRef<number | null>(null);

  const steps = useMemo(() => {
    const base = state.isCasual ? CASUAL_STEPS : ENTHUSIAST_STEPS;
    // Once the user has chosen to skip premium steps, drop them from the tour.
    // While showPremium is null (not asked yet) or true, all steps are included
    // — the opt-in dialog renders in place of the first premium step's content.
    if (showPremium === false) return base.filter(s => !s.isPremium);
    return base;
  }, [state.isCasual, showPremium]);

  const current = steps[stepIndex];
  const isActive = state.phase === 'app-tour' && !!user && !!current;

  // Reset tour state whenever the user (re)enters phase 'app-tour' so replays
  // start from step 0 with the premium opt-in question fresh.
  useEffect(() => {
    if (state.phase === 'app-tour') {
      setStepIndex(0);
      setShowPremium(null);
    }
  }, [state.phase]);

  // After the user opts out of premium, the filtered array shrinks. If the
  // current stepIndex landed past the new end, pull it back.
  useEffect(() => {
    if (showPremium === false && stepIndex >= steps.length) {
      setStepIndex(Math.max(0, steps.length - 1));
    }
  }, [showPremium, steps.length, stepIndex]);

  const lookupTarget = useCallback((targetName: string, attempts = 0) => {
    if (lookupTimerRef.current) {
      window.clearTimeout(lookupTimerRef.current);
      lookupTimerRef.current = null;
    }
    const el = document.querySelector<HTMLElement>(`[data-tour="${targetName}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      window.setTimeout(() => setTargetRect(el.getBoundingClientRect()), 350);
      return;
    }
    if (attempts < 8) {
      lookupTimerRef.current = window.setTimeout(() => lookupTarget(targetName, attempts + 1), 250);
    } else {
      setTargetRect(null);
    }
  }, []);

  // When the active step changes (or tour activates), navigate to its page if needed and look up the target.
  useEffect(() => {
    if (!isActive) return;
    setImgFailed(false);
    setTargetRect(null);

    const currentRoute = location.pathname + (location.search || '');
    if (currentRoute !== current.page) {
      navigate(current.page);
      // Wait for page to render after navigation, then start lookup
      lookupTimerRef.current = window.setTimeout(() => lookupTarget(current.target), 500);
    } else {
      lookupTarget(current.target);
    }

    return () => {
      if (lookupTimerRef.current) {
        window.clearTimeout(lookupTimerRef.current);
        lookupTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, stepIndex, current?.page, current?.target]);

  // Refresh rect on scroll/resize so the spotlight tracks the target.
  useEffect(() => {
    if (!isActive || !current) return;
    const onUpdate = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${current.target}"]`);
      if (el) setTargetRect(el.getBoundingClientRect());
    };
    window.addEventListener('scroll', onUpdate, { passive: true, capture: true });
    window.addEventListener('resize', onUpdate);
    return () => {
      window.removeEventListener('scroll', onUpdate, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', onUpdate);
    };
  }, [isActive, current]);

  const finishTour = useCallback(() => {
    if (state.returnToWelcome) {
      updateState({ returnToWelcome: false });
      setPhase('welcome');
    } else {
      setPhase('first-trip');
    }
  }, [setPhase, state.returnToWelcome, updateState]);

  const handleNext = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      finishTour();
      return;
    }
    setStepIndex(i => i + 1);
  }, [stepIndex, steps.length, finishTour]);

  const handlePrev = useCallback(() => {
    if (stepIndex <= 0) return;
    setStepIndex(i => i - 1);
  }, [stepIndex]);

  const handleSkip = useCallback(() => {
    finishTour();
  }, [finishTour]);

  if (!isActive) return null;

  const suffix = (isManga || isMangaV3) ? '-manga' : isUkiyo ? '-ukiyo' : '';
  const darumaSrc = `/images/mascot/daruma-${current.pose}${suffix}.png`;

  // Spotlight cutout style (or null if target not found — show centered popover with full overlay)
  const cutoutStyle: React.CSSProperties | null = targetRect ? {
    position: 'fixed',
    top: targetRect.top - 8,
    left: targetRect.left - 8,
    width: targetRect.width + 16,
    height: targetRect.height + 16,
    borderRadius: 12,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.65)',
    pointerEvents: 'none',
    transition: 'top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease',
  } : null;

  let popoverPosition: { top: number; left: number };
  if (targetRect) {
    popoverPosition = getPopoverPosition(targetRect, current.position);
  } else {
    // Centered fallback
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
    popoverPosition = {
      top: Math.max(NAV_HEIGHT, (vh - POPOVER_HEIGHT_ESTIMATE) / 2),
      left: Math.max(MARGIN, (vw - POPOVER_WIDTH) / 2),
    };
  }

  const isLastStep = stepIndex >= steps.length - 1;
  const isTransition = !!current.isPageTransition && !isLastStep;
  const showPremiumOptIn = !!current.isPremium && showPremium === null;

  return (
    <>
      {cutoutStyle ? (
        <div style={cutoutStyle} aria-hidden />
      ) : (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.65)',
            zIndex: 8000,
            pointerEvents: 'none',
          }}
          aria-hidden
        />
      )}

      <div
        role="dialog"
        aria-label={showPremiumOptIn ? t('onboarding.premiumOptInTitle') : t(current.titleKey)}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700"
        style={{
          position: 'fixed',
          top: popoverPosition.top,
          left: popoverPosition.left,
          width: `min(${POPOVER_WIDTH}px, calc(100vw - ${MARGIN * 2}px))`,
          zIndex: 8001,
          pointerEvents: 'auto',
          padding: 16,
          transition: 'top 0.25s ease, left 0.25s ease',
        }}
      >
        {showPremiumOptIn ? (
          <>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                {imgFailed ? (
                  <div className="w-12 h-12 rounded-full bg-error-500 flex items-center justify-center text-white text-xl" aria-hidden>
                    🎯
                  </div>
                ) : (
                  <img
                    src={`/images/mascot/daruma-explain${suffix}.png`}
                    alt="Daruma-kun"
                    className="w-12 h-12 object-contain"
                    onError={() => setImgFailed(true)}
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  {t('onboarding.premiumOptInTitle')}
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-200 leading-snug">
                  {t('onboarding.premiumOptInMessage')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
              <Button size="sm" onClick={() => setShowPremium(true)} className="flex-1">
                {t('onboarding.premiumOptInShow')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowPremium(false)} className="flex-1">
                {t('onboarding.premiumOptInSkip')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                {imgFailed ? (
                  <div
                    className="w-12 h-12 rounded-full bg-error-500 flex items-center justify-center text-white text-xl"
                    aria-hidden
                  >
                    🎯
                  </div>
                ) : (
                  <img
                    src={darumaSrc}
                    alt="Daruma-kun"
                    className="w-12 h-12 object-contain"
                    onError={() => setImgFailed(true)}
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  {t(current.titleKey)}
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-200 leading-snug">
                  {t(current.messageKey)}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 gap-2">
              <span className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0">
                {stepIndex + 1} / {steps.length}
              </span>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
                >
                  {t('onboarding.tourSkip')}
                </button>
                {stepIndex > 0 && (
                  <Button size="sm" variant="outline" onClick={handlePrev}>
                    {t('onboarding.tourPrevious')}
                  </Button>
                )}
                <Button size="sm" onClick={handleNext}>
                  {isLastStep
                    ? t('onboarding.tourFinished').split('!')[0] + '!'
                    : isTransition
                      ? t('onboarding.tourGoTo', { page: t(current.titleKey) })
                      : t('onboarding.tourNext')}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
